import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  cancelYowanAdminSeminar,
  fetchYowanAdminNotificationPolicyList,
  fetchYowanAdminSeminarDetail,
} from '../app/yowanContentClient';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const cancellationNotificationKeys = [
  'event.canceled',
  'event.canceled_admin',
  'event.canceled_organizer',
  'event.canceled_instructor',
];

const secondaryActionButtonStyle = {
  background: 'transparent',
  border: '1px solid currentColor',
  color: 'inherit',
};

const fallbackCancellationPolicyLabels = {
  'event.canceled': 'セミナー中止 参加者通知',
  'event.canceled_admin': 'セミナー中止 管理者通知',
  'event.canceled_organizer': 'セミナー中止 主催者通知',
  'event.canceled_instructor': 'セミナー中止 講師通知',
};

const ReadOnlyField = ({ label, value }) => (
  <label className="c-form__field yowan-admin-readonly-field">
    <span className="c-form__label">{label}</span>
    <input className="c-form__control" readOnly type="text" value={value || '-'} />
  </label>
);

const StatusBadge = ({ label, tone = 'draft' }) => (
  <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${tone}`}>
    {label}
  </span>
);

const normalizeSeriesKindLabel = (label = '') => (
  label === '一連の講習会' ? 'シリーズ' : label
);

const DetailSection = ({ title, lead, children }) => (
  <section className="yowan-admin-detail-panel yowan-admin-event-detail__section">
    <header className="yowan-admin-event-detail__section-header">
      <h2 className="yowan-admin-detail-panel__title">{title}</h2>
      {lead ? <p className="yowan-admin-detail-panel__lead">{lead}</p> : null}
    </header>
    {children}
  </section>
);

const buildCancellationPolicyRows = (policies = []) => {
  const policyByKey = policies.reduce((accumulator, policy) => {
    accumulator[policy.eventKey] = policy;
    return accumulator;
  }, {});

  return cancellationNotificationKeys.map((eventKey) => {
    const policy = policyByKey[eventKey] || {};
    const decisionStatus = policy.decisionStatus || 'undecided';

    return {
      eventKey,
      eventName: policy.eventName || fallbackCancellationPolicyLabels[eventKey] || eventKey,
      decisionStatus,
      decisionStatusLabel: policy.decisionStatusLabel || (decisionStatus === 'send' ? '送信する' : decisionStatus === 'skip' ? '送信しない' : '未決定'),
      audienceSummary: policy.audienceSummary || '',
      detailHref: policy.detailHref || `/admin/notifications/policies/${eventKey}`,
    };
  });
};

const buildConfirmPolicySummary = (rows = []) =>
  rows.map((row) => `${row.eventName}: ${row.decisionStatusLabel}${row.audienceSummary ? ` / 宛先: ${row.audienceSummary}` : ''}`).join('\n');

const AdminEventCancelPage = () => {
  const content = yowanPageContent['/admin/events/:eventId/cancel'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [record, setRecord] = useState(null);
  const [policyRows, setPolicyRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCanceling, setIsCanceling] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [policyLoadError, setPolicyLoadError] = useState('');
  const [actionMessage, setActionMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setLoadError('');
    setPolicyLoadError('');
    setActionMessage('');

    Promise.allSettled([
      fetchYowanAdminSeminarDetail(eventId),
      fetchYowanAdminNotificationPolicyList(),
    ])
      .then(([detailResult, policyResult]) => {
        if (!isMounted) {
          return;
        }

        if (detailResult.status === 'fulfilled') {
          setRecord(detailResult.value);
        } else {
          setRecord(null);
          setLoadError('セミナー中止情報を読み込めませんでした。');
        }

        if (policyResult.status === 'fulfilled') {
          setPolicyRows(buildCancellationPolicyRows(policyResult.value.results));
        } else {
          setPolicyRows(buildCancellationPolicyRows([]));
          setPolicyLoadError('通知ルールを読み込めませんでした。現在の通知設定は通知ルール画面で確認してください。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [eventId]);

  const activePolicyRows = useMemo(
    () => policyRows.filter((row) => row.decisionStatus === 'send'),
    [policyRows],
  );

  if (!content) {
    return null;
  }

  const editHref = record ? appendYowanPreviewQueryIfNeeded(`/admin/events/${record.id}/edit?kind=seminar`, preservedDomainSearch) : '';
  const detailHref = record ? appendYowanPreviewQueryIfNeeded(`/admin/events/${record.id}`, preservedDomainSearch) : '';
  const listHref = appendYowanPreviewQueryIfNeeded('/admin/events/seminars', preservedDomainSearch);
  const notificationHref = appendYowanPreviewQueryIfNeeded('/admin/notifications', preservedDomainSearch);
  const isAlreadyCanceled = record?.isActive === false;
  const participantCount = Number(record?.applicationsCount || 0);
  const slotCount = Array.isArray(record?.slots) ? record.slots.length : 0;

  const handleCancelSeminar = async () => {
    if (!record?.id || isCanceling || isAlreadyCanceled) {
      return;
    }

    const confirmMessage = [
      content.confirmMessage,
      '',
      `対象セミナー: ${record.title || record.id}`,
      `参加予定者: ${participantCount}名`,
      `開催コマ: ${slotCount}件`,
      '',
      '現在の通知ルール:',
      buildConfirmPolicySummary(policyRows),
    ].join('\n');

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsCanceling(true);
    setActionMessage('セミナー中止処理を実行しています。');

    try {
      const canceledRecord = await cancelYowanAdminSeminar(record.id);

      setRecord(canceledRecord);
      setActionMessage('セミナー中止を実行しました。送信結果は通知履歴で確認してください。');
      navigate(listHref, { replace: true });
    } catch (error) {
      setActionMessage('セミナー中止を実行できませんでした。入力状態とログイン状態を確認してください。');
    } finally {
      setIsCanceling(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={record ? `${record.title} 中止` : content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="list" to={listHref}>セミナー一覧</Link>,
          detailHref ? <Link key="detail" to={detailHref}>詳細</Link> : null,
          editHref ? <Link key="edit" to={editHref}>編集</Link> : null,
          <Link key="notifications" to={notificationHref}>通知履歴</Link>,
        ]}
      />

      <div className="yowan-admin-form c-surface-content">
        {isLoading ? <LoadingSpinner label="セミナー中止情報を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}

        {!isLoading && !loadError && record ? (
          <div className="yowan-admin-event-detail">
            <DetailSection title="対象セミナー">
              <div className="yowan-admin-event-detail__status-row">
                <StatusBadge label={record.isPublished ? '公開中' : '非公開'} tone={record.isPublished ? 'published' : 'draft'} />
                <StatusBadge label={record.isActive ? '有効' : '停止中'} tone={record.isActive ? 'published' : 'draft'} />
                <StatusBadge label={normalizeSeriesKindLabel(record.seriesKindLabel) || '-'} tone={record.seriesKind === 'grouped' ? 'review' : 'draft'} />
              </div>
              <div className="yowan-admin-event-detail__grid">
                <ReadOnlyField label="セミナー名" value={record.title} />
                <ReadOnlyField label="シリーズ" value={record.series?.title || '-'} />
                <ReadOnlyField label="申込者" value={`${participantCount}名`} />
                <ReadOnlyField label="開催コマ" value={`${slotCount}件`} />
                <ReadOnlyField label="公開パス" value={record.publicPath || record.series?.publicPath || record.href} />
                <ReadOnlyField label="更新日" value={record.updatedAt} />
              </div>
            </DetailSection>

            <DetailSection
              title="実行される処理"
              lead="このページではタイトル・本文・画像・開催コマ内容などの通常項目は保存されません。通常項目を変更したい場合は、先に編集画面で保存してください。"
            >
              <ul className="yowan-admin-detail-list">
                <li>セミナー本体を非公開にします。</li>
                <li>セミナー本体を無効化します。</li>
                <li>関連する開催コマを無効化し、募集停止にします。</li>
                <li>公開面とSEO routeから対象セミナーを外します。</li>
                <li>通知ルールに基づき、セミナー中止通知を発行します。</li>
              </ul>
            </DetailSection>

            <DetailSection
              title="現在の通知ルール"
              lead="以下は現在の通知ルールに基づく送信設定です。実際の送信対象は中止処理時に解決されます。"
            >
              {policyLoadError ? <p className="yowan-admin-form__message">{policyLoadError}</p> : null}
              <div className="yowan-admin-resource-table-wrap">
                <table className="yowan-admin-resource-table">
                  <thead>
                    <tr>
                      <th scope="col">通知</th>
                      <th scope="col">送信判断</th>
                      <th scope="col">宛先概要</th>
                      <th scope="col">確認</th>
                    </tr>
                  </thead>
                  <tbody>
                    {policyRows.map((row) => (
                      <tr key={row.eventKey}>
                        <td>
                          <p className="yowan-admin-resource-table__title">{row.eventName}</p>
                          <p className="yowan-admin-resource-table__subline">{row.eventKey}</p>
                        </td>
                        <td>
                          <StatusBadge
                            label={row.decisionStatusLabel}
                            tone={row.decisionStatus === 'send' ? 'published' : row.decisionStatus === 'skip' ? 'draft' : 'review'}
                          />
                        </td>
                        <td>{row.audienceSummary || '-'}</td>
                        <td>
                          <Link to={appendYowanPreviewQueryIfNeeded(row.detailHref, preservedDomainSearch)}>
                            通知ルール
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="yowan-admin-form__field-note">
                現在「送信する」通知は {activePolicyRows.length} 件です。送信判断や宛先概要を変更する場合は、先に通知ルールを更新してください。
              </p>
            </DetailSection>

            <DetailSection title="中止実行">
              <p className="yowan-admin-detail-panel__lead">
                中止を実行すると、このページで確認した中止処理だけが実行されます。編集中の通常項目はここでは保存されません。
              </p>
              <div className="yowan-admin-form__actions">
                <button
                  type="button"
                  className="c-form__submit"
                  disabled={isCanceling || isAlreadyCanceled}
                  onClick={handleCancelSeminar}
                >
                  {isAlreadyCanceled ? '中止済みまたは無効' : isCanceling ? 'セミナー中止処理中' : 'セミナーを中止する'}
                </button>
                <button
                  type="button"
                  className="c-form__submit"
                  style={secondaryActionButtonStyle}
                  onClick={() => navigate(editHref || listHref)}
                >
                  編集画面に戻る
                </button>
              </div>
              {actionMessage ? <p className="yowan-admin-form__message">{actionMessage}</p> : null}
            </DetailSection>
          </div>
        ) : null}
      </div>
    </AdminBlogShell>
  );
};

export default AdminEventCancelPage;
